/* =============================================================================
 * Arachne Audio - unified cart grouping on the /cart PAGE
 *
 * Companion to assets/cart-drawer-groups.js. The controller in that file is hard-
 * wired to <cart-drawer>; this small controller does the SAME job for the stock
 * Dawn /cart PAGE (sections/main-cart-items.liquid). It REUSES the shared grouping
 * model + DOM card renderers exported on window.AcfgCartGroups, so the drawer and
 * the page can never diverge (no grouping/render logic is duplicated here).
 *
 * It reads the page data island (#MainCart-AcfgData), folds a configured cable
 * (base line + hidden add-on lines sharing _config_id) into ONE card with the
 * summed price, atomic remove (every line at once) + 6s undo, atomic group
 * quantity, and Edit / Keep configuring. Plain products render as ordinary single
 * rows in the same list. Edit reuses the SAME cross-page handoff as the drawer
 * (sessionStorage 'acfg_prefill' + navigate to the product), so the configurator
 * engine pre-fills identically.
 *
 * Self-contained: it runs its own /cart AJAX + Section Rendering refetch and does
 * NOT depend on Dawn's cart.js. cart.js is still loaded on /cart (by
 * main-cart-items.liquid, as cart_type is "page"); its <cart-items> element skips the
 * cartUpdate events published here (source "cart-items"), so the two never both
 * refetch the same change. FAIL-SAFE CONTRACT: every enhancement path is wrapped in
 * try/catch; on ANY error the stock Dawn cart table is left fully intact and
 * visible (it is only hidden once the grouped view is built successfully).
 * ========================================================================== */
(function () {
  "use strict";

  if (typeof document === "undefined" || !document.querySelector) return;
  // Idempotent: a second include on the same page must not re-bind listeners.
  if (window.__acfgCartPageBound) return;
  window.__acfgCartPageBound = true;

  var API = window.AcfgCartGroups;
  // Shared module missing/too old -> do nothing; the stock Dawn cart stays intact.
  if (!API || typeof API.groupLines !== "function" || typeof API.renderView !== "function") return;

  var UNDO_MS = 6000;

  var observer = null;
  var enhanceQueued = false;
  var busy = false;
  var currentView = [];
  var currentFormat = "";
  var undoState = null; // { items, model, timer }
  // Control to re-focus after the next redraw: { action, attr, id }. Set only
  // when the clicked button really had keyboard focus (Dawn parity: cart.js
  // re-focuses the quantity input after an update).
  var pendingFocus = null;
  var refocusAfterCount = false;

  /* ------------------------------ tiny helpers ---------------------------- */
  function el(tag, cls, html) {
    if (API.el) return API.el(tag, cls, html);
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }
  function txt(s) { return document.createTextNode(s == null ? "" : String(s)); }
  function disp(s) { return API.displayValue ? API.displayValue(s) : String(s == null ? "" : s); }
  /* Localized UI strings: same JSON island contract as cart-drawer-groups.js
   * (<script type="application/json" data-aa-cart-strings>, emitted by the
   * Liquid before these scripts load, translated via locales cart_groups.*).
   * Read once, lazily; hard fallback to the previous English literals so a
   * missing/broken island can never break rendering. Mini-interpolator fills
   * %{model}/%{title}. */
  var AA_STRING_DEFAULTS = {
    edit: "Edit",
    build_another: "Build another",
    remove: "Remove",
    undo: "Undo",
    removed_model: "Removed %{model}.",
    qty_decrease: "Decrease quantity",
    qty_increase: "Increase quantity",
    remove_title: "Remove %{title}",
    restored: "Item restored to cart."
  };
  var aaStrings = null;
  function aaT(key, vars) {
    if (!aaStrings) {
      aaStrings = {};
      try {
        var node = document.querySelector("script[data-aa-cart-strings]");
        if (node) aaStrings = JSON.parse(node.textContent || "{}") || {};
      } catch (e) { aaStrings = {}; }
    }
    var s = aaStrings[key];
    if (typeof s !== "string" || !s) s = AA_STRING_DEFAULTS[key] || "";
    if (vars) {
      for (var k in vars) {
        if (Object.prototype.hasOwnProperty.call(vars, k)) {
          var v = vars[k] == null ? "" : String(vars[k]);
          // Two token forms: %{k} (locale convention / fallback defaults) and @@K@@
          // (the Liquid island's brace-free sentinel — literal %{...} braces break
          // Shopify's tag parser).
          s = s.split("@@" + k.toUpperCase() + "@@").join(v);
          s = s.split("%{" + k + "}").join(v);
        }
      }
    }
    return s;
  }
  function routeUrl(key, fallback) { return (window.routes && window.routes[key]) || fallback; }
  function sectionIdOf(domId, fallback) {
    var node = document.getElementById(domId);
    return (node && node.dataset && node.dataset.id) || fallback;
  }

  function announce(message) {
    var live = document.getElementById("cart-live-region-text");
    if (live) { live.setAttribute("aria-hidden", "false"); live.textContent = message; }
  }
  // Focus is "lost" when it sits on <body> or on a node that is no longer rendered
  // (a redraw removed or hid it). Only then do we move it: never steal focus
  // from wherever the customer went while the request was running.
  function focusIsLost() {
    var a = document.activeElement;
    if (!a || a === document.body || a === document.documentElement) return true;
    if (!a.isConnected) return true;
    return !(a.getClientRects && a.getClientRects().length);
  }
  function focusQuietly(node) {
    try { node.focus({ preventScroll: true }); } catch (e) { try { node.focus(); } catch (e2) {} }
    return document.activeElement === node;
  }
  // Visible cart heading: the "Your cart" h1, or the empty-cart h1 once the cart
  // is empty (component-cart.css hides the former then). tabindex is added only
  // while it holds focus, so nothing changes for mouse users or the tab order.
  function focusHeading() {
    var ci = document.querySelector("cart-items");
    if (!ci) return;
    var h = ci.classList.contains("is-empty") ? ci.querySelector(".cart__empty-text") : ci.querySelector(".title--primary");
    if (!h) return;
    if (!h.hasAttribute("tabindex")) {
      h.setAttribute("tabindex", "-1");
      h.addEventListener("blur", function () { h.removeAttribute("tabindex"); }, { once: true });
    }
    focusQuietly(h);
  }
  var STEP_SIBLING = { "group-dec": "group-inc", "group-inc": "group-dec", "line-dec": "line-inc", "line-inc": "line-dec" };
  function findControl(host, action, attr, id) {
    var nodes = host.querySelectorAll('[data-aa-action="' + action + '"]');
    for (var i = 0; i < nodes.length; i++) if (nodes[i].getAttribute(attr) === id) return nodes[i];
    return null;
  }
  // Called after enhance() rebuilt the cards.
  function restoreFocus() {
    var want = pendingFocus;
    // A redraw while the request is still running (something else changed the
    // cart markup) keeps the target for the redraw that follows the update.
    if (!busy) pendingFocus = null;
    if (!want || !focusIsLost()) return;
    var host = document.getElementById("MainCart-AcfgGroups");
    var node = null;
    if (host && !host.hasAttribute("hidden")) {
      node = findControl(host, want.action, want.attr, want.id);
      // e.g. "-" is disabled at quantity 1: use the other stepper button.
      if (node && node.disabled && STEP_SIBLING[want.action]) node = findControl(host, STEP_SIBLING[want.action], want.attr, want.id);
    }
    if (!(node && !node.disabled && focusQuietly(node))) focusHeading();
  }

  function showError(message) {
    var box = document.getElementById("cart-errors") || document.getElementById("CartDrawer-CartErrors");
    if (box) box.textContent = message || "";
    else if (message && window.console && console.warn) console.warn("[acfg-cart-page] " + message);
  }

  /* ----------------------------- enhancement ------------------------------ */
  function scheduleEnhance() {
    if (enhanceQueued) return;
    enhanceQueued = true;
    window.setTimeout(function () { enhanceQueued = false; enhance(); }, 0);
  }

  function enhance() {
    var cartItems = document.querySelector("cart-items");
    if (!cartItems) return;
    var dataNode = document.getElementById("MainCart-AcfgData");
    var host = document.getElementById("MainCart-AcfgGroups");
    // Empty cart, or island/host not present -> leave the stock markup exactly as
    // Dawn rendered it (Dawn's own empty state shows via the is-empty class).
    if (!dataNode || !host) { cartItems.classList.remove("aa-cart-enhanced"); return; }

    if (observer) observer.disconnect();
    try {
      var data = JSON.parse(dataNode.textContent || "{}");
      currentFormat = API.moneyFormatOf ? API.moneyFormatOf(data) : (data.money_format || "");
      currentView = API.groupLines(data.lines || []);
      var frag = API.renderView(currentView, currentFormat);
      host.innerHTML = "";
      host.appendChild(frag);
      host.removeAttribute("hidden");
      cartItems.classList.add("aa-cart-enhanced");
      // Errors are cleared when the next action starts, not here: a redraw
      // follows every update and would wipe the message that update set.
    } catch (e) {
      // Fail-safe: revert to the stock flat table, never break the cart page.
      try {
        cartItems.classList.remove("aa-cart-enhanced");
        host.innerHTML = "";
        host.setAttribute("hidden", "");
      } catch (e2) {}
      if (window.console && console.error) console.error("[acfg-cart-page] grouping failed; stock cart shown", e);
    } finally {
      reconnect();
      restoreFocus();
    }
  }

  // Observe the STABLE <cart-items> element (Dawn swaps its inner HTML, never the
  // element itself), so an external section refetch re-triggers enhancement.
  function reconnect() {
    var target = document.querySelector("cart-items");
    if (!target) return;
    if (!observer) observer = new MutationObserver(function () { scheduleEnhance(); });
    try { observer.observe(target, { childList: true, subtree: true }); } catch (e) {}
  }

  /* ------------------------- cart mutations (AJAX) ------------------------ */
  function requestedSections() {
    var arr = [sectionIdOf("main-cart-items", "main-cart-items")];
    var footer = sectionIdOf("main-cart-footer", null);
    if (footer) arr.push(footer);
    // Only a stock Dawn header has #cart-icon-bubble; the custom header does not.
    if (document.getElementById("cart-icon-bubble")) arr.push("cart-icon-bubble");
    // Dawn's "New estimated total" announcement (sections/cart-live-region-text.liquid).
    arr.push("cart-live-region-text");
    return arr;
  }

  // Text for a failed cart request. Shopify's own 4xx cart message (stock or
  // purchase limit, already in the shopper's language) is shown as is; anything
  // else (dropped connection, 429 or 5xx, an HTML challenge page, a script error)
  // gets the theme's translated cart error, as Dawn's cart.js does.
  function cartErrorText(err) {
    if (err && err.aaShopify && err.message) return err.message;
    return (window.cartStrings && window.cartStrings.error) || "There was an error while updating your cart. Please try again.";
  }
  function postCart(url, payload) {
    payload.sections = requestedSections();
    payload.sections_url = window.location.pathname;
    return fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify(payload)
    }).then(function (res) {
      return res.json().then(function (body) {
        if (!res.ok) {
          var msg = body && (body.description || body.message);
          var e = new Error(typeof msg === "string" ? msg : "");
          e.aaShopify = typeof msg === "string" && msg !== "" && res.status >= 400 && res.status < 500 && res.status !== 429;
          throw e;
        }
        return body;
      });
    });
  }

  function sectionInner(html, selector) {
    try {
      var doc = new DOMParser().parseFromString(html, "text/html");
      var node = doc.querySelector(selector);
      return node ? node.innerHTML : null;
    } catch (e) { return null; }
  }
  function swapJsContents(containerId, html) {
    if (!html) return;
    var container = document.getElementById(containerId);
    if (!container) return;
    var dst = container.querySelector(".js-contents");
    var inner = sectionInner(html, ".js-contents");
    if (dst && inner != null) dst.innerHTML = inner;
  }
  function updateBubble(html) {
    if (!html) return;
    var bubble = document.getElementById("cart-icon-bubble");
    if (!bubble) return;
    var target = bubble.querySelector(".shopify-section") || bubble;
    var inner = sectionInner(html, ".shopify-section");
    if (inner != null) target.innerHTML = inner;
  }
  function updateLiveRegion(html) {
    if (!html) return;
    try {
      var node = new DOMParser().parseFromString(html, "text/html").querySelector(".shopify-section");
      var text = node ? (node.textContent || "").replace(/\s+/g, " ").trim() : "";
      if (text) announce(text);
    } catch (e) {}
  }
  // This store's custom header shows the count in [data-cart-count], not a
  // #cart-icon-bubble section, so keep it live too (mirrors the header's format).
  function updateCartCount(count) {
    if (typeof count !== "number") return;
    var label = count > 99 ? "99+" : (count > 0 ? String(count) : "");
    var nodes = document.querySelectorAll("[data-cart-count]");
    for (var i = 0; i < nodes.length; i++) nodes[i].textContent = label;
  }

  // Header badge count: skip the configurator's hidden add-on lines, matching
  // what the header's own Liquid renders (sections/custom-header.liquid).
  function visibleItemCount(cart) {
    if (!cart || !cart.items) return cart ? cart.item_count : 0;
    var n = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var hidden = cart.items[i].properties && cart.items[i].properties._hidden;
      if (!(hidden === true || hidden === "true")) n += cart.items[i].quantity || 0;
    }
    return n;
  }
  function applyItemCount(count, badgeCount) {
    var cartItems = document.querySelector("cart-items");
    var footer = document.getElementById("main-cart-footer");
    if (cartItems) cartItems.classList.toggle("is-empty", count === 0);
    if (footer) footer.classList.toggle("is-empty", count === 0);
    updateCartCount(typeof badgeCount === "number" ? badgeCount : count);
  }
  // /cart/add.js responses (used by Undo) carry no item_count; read it from
  // /cart.js so a restored item doesn't stay hidden behind the empty state.
  function refreshItemCount() {
    fetch(routeUrl("cart_url", "/cart") + ".js", { headers: { "Accept": "application/json" } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        if (cart && typeof cart.item_count === "number") applyItemCount(cart.item_count, visibleItemCount(cart));
        if (refocusAfterCount) { refocusAfterCount = false; if (focusIsLost()) focusHeading(); }
      })
      .catch(function () { refocusAfterCount = false; });
  }

  function applySections(state) {
    if (!state) return;
    if (state.sections) {
      swapJsContents("main-cart-items", state.sections[sectionIdOf("main-cart-items", "main-cart-items")]);
      swapJsContents("main-cart-footer", state.sections[sectionIdOf("main-cart-footer", "main-cart-footer")]);
      updateBubble(state.sections["cart-icon-bubble"]);
      updateLiveRegion(state.sections["cart-live-region-text"]);
    }
    if (typeof state.item_count === "number") applyItemCount(state.item_count, visibleItemCount(state));
    else refreshItemCount();
    // Let the rest of the theme sync. Source "cart-items" matches the drawer
    // controller and stops Dawn's cart-items / drawer-items subscribers from
    // firing a second, redundant section refetch (they ignore that source).
    try {
      if (typeof publish === "function" && window.PUB_SUB_EVENTS) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-items", cartData: state });
      }
    } catch (e) {}
  }

  function getJson(url, err) {
    return fetch(url, { headers: { "Accept": "application/json" } }).then(function (res) {
      if (!res.ok) throw err;
      return res.json();
    });
  }
  // Same quantity check as the drawer (see updateCart in cart-drawer-groups.js).
  function readCart(err) {
    return getJson(routeUrl("cart_url", "/cart") + ".js", err).catch(function () { throw err; });
  }
  function withSections(cart) {
    return getJson(window.location.pathname + "?sections=" + requestedSections().join(","), null)
      .then(function (sections) { cart.sections = sections; return cart; }, function () { return cart; });
  }
  // Keeps Edit's quantity record in step with the cart (syncEditQty in
  // assets/cart-drawer-groups.js). `partial`: the response of an Undo add.
  function syncEditQty(state, partial) {
    if (state && typeof API.syncEditQty === "function") API.syncEditQty(state.items, partial);
  }

  // `check` ({ lines, qty, cid? }) marks a quantity change of one group or line.
  function updateCart(updates, check) {
    if (busy) return Promise.resolve(null);
    showError("");
    busy = true;
    var host = document.getElementById("MainCart-AcfgGroups");
    if (host) host.classList.add("aa-busy");
    var url = routeUrl("cart_update_url", "/cart/update.js");
    var notice = "", failed = false;
    var request = postCart(url, { updates: updates });
    if (check && typeof API.buildResyncUpdates === "function") {
      request = request
        .catch(function (err) {
          // A stock 422 can still have applied part of the update.
          failed = true;
          notice = cartErrorText(err);
          return readCart(err);
        })
        .then(function (state) {
          var r = state.items ? API.buildResyncUpdates(check.lines, check.qty, state.items, check.cid) : null;
          // After a failure that changed nothing the server's message is the reason.
          if (r && r.capped && (!failed || r.changed)) notice = API.stockMessage(r.quantity, window.cartStrings && window.cartStrings.quantityError);
          function asIs() { return state.sections ? state : withSections(state); }
          if (r && r.updates) {
            // If the fix fails, still redraw the cart as it now is.
            return postCart(url, { updates: r.updates }).catch(function (err) {
              notice = notice || cartErrorText(err);
              return asIs();
            });
          }
          return asIs();
        });
    }
    return request
      // state is the whole cart here: keep Edit's quantity record in step.
      .then(function (state) { syncEditQty(state); applySections(state); scheduleEnhance(); showError(notice); return state; })
      .catch(function (err) { pendingFocus = null; showError(cartErrorText(err)); return null; })
      .then(function (state) {
        busy = false;
        if (host) host.classList.remove("aa-busy");
        return state;
      });
  }

  /* -------------------------------- undo ---------------------------------- */
  function clearUndo(keepFocus) {
    if (undoState && undoState.timer) window.clearTimeout(undoState.timer);
    undoState = null;
    var t = document.getElementById("MainCart-AcfgToast");
    var hadFocus = !!(t && t.contains(document.activeElement));
    if (t && t.parentNode) t.parentNode.removeChild(t);
    // The toast held focus (its Undo button is focused on show): hand it to the
    // cart heading instead of dropping it to the top of the document.
    if (hadFocus && keepFocus) focusHeading();
    return hadFocus;
  }
  function showUndo(model, items) {
    clearUndo();
    var toast = el("div", "aa-toast aa-toast--page");
    toast.id = "MainCart-AcfgToast";
    toast.setAttribute("role", "status");
    var msg = el("span", "aa-toast__msg");
    msg.appendChild(txt(aaT("removed_model", { model: model || "item" })));
    var undoBtn = el("button", "aa-toast__undo");
    undoBtn.type = "button";
    undoBtn.setAttribute("data-aa-action", "undo");
    undoBtn.appendChild(txt(aaT("undo")));
    toast.appendChild(msg);
    toast.appendChild(undoBtn);
    document.body.appendChild(toast);
    undoState = { items: items, model: model, timer: window.setTimeout(function () { clearUndo(true); }, UNDO_MS) };
    try { undoBtn.focus(); } catch (e) {}
  }
  function doUndo() {
    if (!undoState || busy) return;
    var items = undoState.items;
    // If the cart was empty, the heading moves from the empty-cart h1 back to
    // "Your cart" once the item count is refreshed: check focus again then.
    refocusAfterCount = clearUndo(true);
    showError("");
    busy = true;
    var host = document.getElementById("MainCart-AcfgGroups");
    if (host) host.classList.add("aa-busy");
    postCart(routeUrl("cart_add_url", "/cart/add.js"), { items: items })
      .then(function (state) { syncEditQty(state, true); applySections(state); scheduleEnhance(); announce(aaT("restored")); })
      .catch(function (err) { refocusAfterCount = false; showError(cartErrorText(err)); })
      .then(function () { busy = false; if (host) host.classList.remove("aa-busy"); });
  }

  /* -------------------------- edit / keep configuring --------------------- */
  // Cross-page handoff identical to the drawer: stash the visible option map under
  // the SAME sessionStorage key and navigate to the product page, where the engine
  // consumes it on init (and only applies it when _model matches the product).
  function stashPrefill(group) {
    var model = group.model || "";
    var props = (group.base && group.base.properties) || {};
    var map = {};
    var keys = Object.keys(props);
    for (var i = 0; i < keys.length; i++) { if (keys[i].charAt(0) !== "_") map[keys[i]] = props[keys[i]]; }
    // The edited group's line keys ride along so the engine can REPLACE the old
    // lines when (and only when) the re-configured cable is successfully added.
    var lineKeys = [];
    for (var lk = 0; lk < group.lines.length; lk++) { if (group.lines[lk] && group.lines[lk].key) lineKeys.push(group.lines[lk].key); }
    try { window.sessionStorage.setItem("acfg_prefill", JSON.stringify({ model: model, props: map, replace_keys: lineKeys })); } catch (e) {}
  }
  function editGroup(group) {
    stashPrefill(group);
    // The engine re-adds an edited build at quantity 1; the configurator page
    // restores this quantity after the replacing add (snippets/cable-edit-qty.liquid).
    // Written on every Edit, so an older record is always replaced.
    try {
      window.sessionStorage.setItem("acfg_edit_qty", JSON.stringify({ model: group.model || "", qty: group.quantity || 1, cid: group.configId || "", t: Date.now() }));
    } catch (e) {}
    // Mark this group as being edited so the drawer controller removes it once the
    // edited cable is added (see onConfiguredAdd in assets/cart-drawer-groups.js).
    if (group.configId) {
      try {
        window.sessionStorage.setItem("acfg_replace", JSON.stringify({ cid: group.configId, model: group.model || "", t: Date.now() }));
      } catch (e) {}
    }
    var url = group.base && group.base.url;
    if (url) window.location.href = url;
  }
  // "Build another": open a fresh configurator for this model (no prefill).
  function keepConfiguring(group) {
    var url = group.base && group.base.url;
    if (url) window.location.href = url;
  }

  /* ------------------------------ interactions ---------------------------- */
  function findGroup(cid) {
    for (var i = 0; i < currentView.length; i++) {
      if (currentView[i].type === "group" && currentView[i].configId === cid) return currentView[i];
    }
    return null;
  }
  function findLine(key) {
    for (var i = 0; i < currentView.length; i++) {
      if (currentView[i].type === "product" && currentView[i].line.key === key) return currentView[i].line;
    }
    return null;
  }

  function onClick(e) {
    var btn = e.target && e.target.closest ? e.target.closest("[data-aa-action]") : null;
    if (!btn) return;
    // Only handle actions inside the PAGE's own host/toast. The drawer controller
    // owns #CartDrawer-* and is scoped the same way, so the two never collide even
    // though both bind a single document-level click listener.
    if (!btn.closest("#MainCart-AcfgGroups") && !btn.closest("#MainCart-AcfgToast")) return;

    var action = btn.getAttribute("data-aa-action");
    if (action === "undo") { e.preventDefault(); doUndo(); return; }

    var cid = btn.getAttribute("data-aa-cid");
    var key = btn.getAttribute("data-aa-key");
    if (!busy) {
      pendingFocus = (STEP_SIBLING[action] && btn === document.activeElement)
        ? { action: action, attr: cid ? "data-aa-cid" : "data-aa-key", id: cid || key }
        : null;
    }
    var group = cid ? findGroup(cid) : null;
    var line = key ? findLine(key) : null;

    if (action === "remove-group" && group) {
      e.preventDefault();
      var items = API.buildUndoItems(group);
      var model = group.model;
      // The undo toast (role="status") announces the removal; no double-announce.
      updateCart(API.buildRemoveUpdates(group)).then(function (state) { if (state) showUndo(model, items); });
    } else if (action === "group-inc" && group) {
      e.preventDefault();
      var iq = (group.quantity || 1) + 1;
      updateCart(API.buildSetQtyUpdates(group, iq), { lines: group.lines, qty: iq, cid: group.configId });
    } else if (action === "group-dec" && group) {
      e.preventDefault();
      var nq = (group.quantity || 1) - 1;
      if (nq >= 1) updateCart(API.buildSetQtyUpdates(group, nq), { lines: group.lines, qty: nq, cid: group.configId });
    } else if (action === "edit" && group) {
      e.preventDefault();
      editGroup(group);
    } else if (action === "keep" && group) {
      e.preventDefault();
      keepConfiguring(group);
    } else if (action === "remove-line" && line) {
      e.preventDefault();
      var lineItems = [{ id: line.variant_id, quantity: line.quantity, properties: line.properties || {} }];
      var lineTitle = disp(line.title);
      // The undo toast (role="status") announces the removal; no double-announce.
      var u = {}; u[line.key] = 0;
      updateCart(u).then(function (state) { if (state) showUndo(lineTitle, lineItems); });
    } else if (action === "line-inc" && line) {
      e.preventDefault();
      var lu = {}; lu[line.key] = (line.quantity || 1) + 1;
      updateCart(lu, { lines: [line], qty: lu[line.key] });
    } else if (action === "line-dec" && line) {
      e.preventDefault();
      var ld = (line.quantity || 1) - 1;
      if (ld >= 1) { var d = {}; d[line.key] = ld; updateCart(d, { lines: [line], qty: ld }); }
    }
  }

  /* --------------------------------- init --------------------------------- */
  function init() {
    if (!document.querySelector("cart-items")) return; // not the /cart page
    document.addEventListener("click", onClick);
    reconnect();
    enhance();
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();

